import React, { useCallback, useEffect, useRef, useState } from 'react';

export const WHEEL_ITEM_HEIGHT = 36;
export const WHEEL_RICH_ITEM_HEIGHT = 40;
export const WHEEL_VISIBLE_ROWS = 5;

const HOUR_OPTIONS = [...Array(24).keys()].map(i => String(i).padStart(2, '0'));
const MINSEC_OPTIONS = [...Array(60).keys()].map(i => String(i).padStart(2, '0'));
/** UI shows km with three decimal places; API distance steps remain in meters. */
const DIST_KM_OPTIONS = [...Array(501).keys()].map(i => String(i));
const DIST_FRAC_OPTIONS = [...Array(1000).keys()].map(i => String(i).padStart(3, '0'));

const SNAP_DEBOUNCE_MS = 80;

interface WheelPickerProps {
  options: string[];
  renderOption?: (option: string, index: number) => React.ReactNode;
  selected: number;
  onChange: (index: number) => void;
  className?: string;
  /** `rich` — wider labels e.g. "38% (185 W)" for intensity range pickers. */
  variant?: 'compact' | 'rich';
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(Math.max(Math.round(index), 0), length - 1);
}

/** Scroll-snap column picker — web substitute for Ai-Tempo Wheely. */
export function WheelPicker({
  options,
  renderOption,
  selected,
  onChange,
  className,
  variant = 'compact',
}: WheelPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const snapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const suppressScrollRef = useRef(false);
  const isRich = variant === 'rich';
  const itemHeight = isRich ? WHEEL_RICH_ITEM_HEIGHT : WHEEL_ITEM_HEIGHT;
  const viewportHeight = itemHeight * WHEEL_VISIBLE_ROWS;

  const [activeIndex, setActiveIndex] = useState(() => clampIndex(selected, options.length));

  const scrollToIndex = useCallback(
    (index: number, smooth: boolean) => {
      const el = ref.current;
      if (!el) return;
      const clamped = clampIndex(index, options.length);
      const top = clamped * itemHeight;
      suppressScrollRef.current = true;
      if (smooth && 'scrollTo' in el) {
        el.scrollTo({ top, behavior: 'smooth' });
      } else {
        el.scrollTop = top;
      }
      window.setTimeout(() => {
        suppressScrollRef.current = false;
      }, smooth ? 220 : 0);
      setActiveIndex(clamped);
    },
    [itemHeight, options.length],
  );

  /** External selected prop → scroll position (modal open / value restore). */
  useEffect(() => {
    const clamped = clampIndex(selected, options.length);
    setActiveIndex(clamped);
    const el = ref.current;
    if (!el) return;
    const target = clamped * itemHeight;
    if (Math.abs(el.scrollTop - target) > 1) {
      scrollToIndex(clamped, false);
    }
  }, [selected, options.length, itemHeight, scrollToIndex]);

  useEffect(() => () => {
    if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  const commitIndex = useCallback(
    (index: number) => {
      const clamped = clampIndex(index, options.length);
      setActiveIndex(clamped);
      scrollToIndex(clamped, true);
      if (clamped !== selected) onChange(clamped);
    },
    [onChange, options.length, scrollToIndex, selected],
  );

  const handleScroll = () => {
    if (suppressScrollRef.current) return;
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const idx = clampIndex(Math.round(el.scrollTop / itemHeight), options.length);
      setActiveIndex(idx);
    });

    if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    snapTimerRef.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = clampIndex(Math.round(el.scrollTop / itemHeight), options.length);
      commitIndex(idx);
    }, SNAP_DEBOUNCE_MS);
  };

  const rootClass = [
    'wb-wheel',
    'wb-wheel-ltr',
    isRich ? 'wb-wheel--rich' : 'wb-wheel--compact',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rootClass}
      dir="ltr"
      style={{
        ['--wb-wheel-item-height' as string]: `${itemHeight}px`,
        ['--wb-wheel-height' as string]: `${viewportHeight}px`,
      }}>
      <div
        ref={ref}
        className="wb-wheel__scroll"
        onScroll={handleScroll}
        onTouchEnd={() => {
          const el = ref.current;
          if (!el) return;
          commitIndex(Math.round(el.scrollTop / itemHeight));
        }}
        onMouseUp={() => {
          const el = ref.current;
          if (!el) return;
          commitIndex(Math.round(el.scrollTop / itemHeight));
        }}>
        <div className="wb-wheel__pad" />
        {options.map((opt, i) => (
          <div
            key={`${i}-${opt}`}
            className={`wb-wheel__item${i === activeIndex ? ' wb-wheel__item--active' : ''}`}
            onClick={() => commitIndex(i)}>
            {renderOption ? renderOption(opt, i) : opt}
          </div>
        ))}
        <div className="wb-wheel__pad" />
      </div>
      <div className="wb-wheel__highlight" aria-hidden />
    </div>
  );
}

interface WheelPairProps {
  left: React.ReactNode;
  right: React.ReactNode;
  className?: string;
}

export function WheelPair({ left, right, className }: WheelPairProps) {
  return (
    <div className={`wb-wheel-pair wb-wheel-ltr${className ? ` ${className}` : ''}`} dir="ltr">
      {left}
      <div className="wb-wheel-pair__sep" aria-hidden />
      {right}
    </div>
  );
}

/** H : M : S — three equal columns (matches Ai-Tempo duration picker). */
export function TimeWheelRow({
  hours,
  minutes,
  seconds,
}: {
  hours: React.ReactNode;
  minutes: React.ReactNode;
  seconds: React.ReactNode;
}) {
  return (
    <div className="wb-time-wheels wb-wheel-ltr" dir="ltr">
      {hours}
      <div className="wb-wheel-pair__sep" aria-hidden />
      {minutes}
      <div className="wb-wheel-pair__sep" aria-hidden />
      {seconds}
    </div>
  );
}

export { HOUR_OPTIONS, MINSEC_OPTIONS, DIST_KM_OPTIONS, DIST_FRAC_OPTIONS };
